import { useCallback, useEffect, useState } from "react";
import { useStore } from "@/lib/store";
import { navigate as navigateRoute, pushPath } from "@/lib/router";
import { BACKEND_URL } from "@/lib/backend";
import { continueInNewChat, suggestChatTitle } from "@/lib/api";
import { ApiKeyDialog } from "../ApiKeyGate";
import { ConfirmDialog } from "../ConfirmDialog";
import { ChatHeader, RenameChatDialog } from "./ChatHeader";
import { ChatInput } from "./ChatInput";
import { HomeIntro } from "./HomeIntro";
import { MessageList } from "./MessageList";
import { RequestBar } from "./RequestBar";
import { ArtifactStrip } from "../artifacts/ArtifactStrip";
import { getChatIdFromUrl, useChatUrlSync, useEdgeSwipeToOpen } from "@/hooks/useUrlSync";
import { useChatRun } from "@/hooks/useChatRun";

export function ChatView() {
  const conversations = useStore((s) => s.conversations);
  const activeId = useStore((s) => s.activeId);
  const models = useStore((s) => s.models);
  const activeModel = useStore((s) => s.activeModel);
  const loaded = useStore((s) => s.loaded);
  const folders = useStore((s) => s.folders);
  const init = useStore((s) => s.init);
  const selectConversation = useStore((s) => s.selectConversation);
  const startNewChat = useStore((s) => s.startNewChat);
  const deleteConversation = useStore((s) => s.deleteConversation);
  const setActiveTitle = useStore((s) => s.setActiveTitle);
  const moveChat = useStore((s) => s.moveChat);
  const togglePin = useStore((s) => s.togglePin);
  const artifactPanelOpen = useStore((s) => s.artifactPanelOpen);
  const openArtifactPanel = useStore((s) => s.openArtifactPanel);
  const sidebarOpen = useStore((s) => s.sidebarOpen);
  const setSidebarOpen = useStore((s) => s.setSidebarOpen);
  const activeRunChatIds = useStore((s) => s.activeRunChatIds);
  const showApiKey = useStore((s) => s.showApiKey);
  const setShowApiKey = useStore((s) => s.setShowApiKey);

  const [initError, setInitError] = useState<string | null>(null);
  const [inlineArtifactExpanded, setInlineArtifactExpanded] = useState(false);
  const [confirmingDeleteChat, setConfirmingDeleteChat] = useState<string | null>(null);
  const [renamingChat, setRenamingChat] = useState<{ id: string; draft: string } | null>(null);
  const [continueBusy, setContinueBusy] = useState(false);
  const [suggestBusy, setSuggestBusy] = useState(false);
  const refreshConversations = useStore((s) => s.refreshConversations);

  const navigateTo = useCallback((id: string | null) => {
    pushPath(id ? `/chat/${id}` : "/");
  }, []);

  const { isStreaming, displayMessages, lastUsage, sendMessage, reloadLast, stop } = useChatRun({
    onChatCreated: navigateTo,
  });

  useEdgeSwipeToOpen(!sidebarOpen, () => setSidebarOpen(true));

  useEffect(() => {
    init().then(() => {
      const id = getChatIdFromUrl();
      if (id) selectConversation(id).catch(() => {});
    }).catch((err) => {
      console.error("init() failed:", err);
      const detail = err instanceof Error
        ? `${err.name}: ${err.message}${err.stack ? `\n\n${err.stack}` : ""}`
        : String(err);
      setInitError(detail);
    });
  }, []);

  useChatUrlSync({
    onSelect: useCallback((id: string) => {
      selectConversation(id).catch(() => startNewChat());
    }, [selectConversation, startNewChat]),
    onNew: startNewChat,
  });

  const handleNew = useCallback(() => {
    startNewChat();
    navigateTo(null);
    setSidebarOpen(false);
  }, [navigateTo, startNewChat, setSidebarOpen]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod) return;
      if (e.key.toLowerCase() === "n") {
        e.preventDefault();
        handleNew();
      }
      if (e.key.toLowerCase() === "s") {
        e.preventDefault();
        setSidebarOpen(!useStore.getState().sidebarOpen);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleNew, setSidebarOpen]);

  const handleDelete = useCallback(async (id: string) => {
    await deleteConversation(id);
    if (activeId === id) navigateTo(null);
  }, [activeId, navigateTo, deleteConversation]);

  const handleSelect = useCallback(async (id: string) => {
    await selectConversation(id);
    navigateTo(id);
  }, [navigateTo, selectConversation]);

  const handleRenameSubmit = useCallback(async () => {
    if (!renamingChat) return;
    const name = renamingChat.draft.trim();
    const id = renamingChat.id;
    setRenamingChat(null);
    if (name) await setActiveTitle(id, name);
  }, [renamingChat, setActiveTitle]);

  const handleSuggestTitle = useCallback(async () => {
    if (!activeId || suggestBusy) return;
    setSuggestBusy(true);
    try {
      await setActiveTitle(activeId, await suggestChatTitle(activeId));
    } catch (e) {
      alert(e instanceof Error ? e.message : String(e));
    } finally {
      setSuggestBusy(false);
    }
  }, [activeId, suggestBusy, setActiveTitle]);

  const handleContinueInNew = useCallback(async () => {
    if (!activeId || continueBusy) return;
    setContinueBusy(true);
    try {
      const result = await continueInNewChat(activeId);
      await refreshConversations();
      await selectConversation(result.chatId);
      navigateTo(result.chatId);
    } catch (e) {
      console.error("continue_in_new_chat failed:", e);
      alert(e instanceof Error ? e.message : String(e));
    } finally {
      setContinueBusy(false);
    }
  }, [activeId, continueBusy, refreshConversations, selectConversation, navigateTo]);

  const confirmDeleteChat = useCallback(async () => {
    const id = confirmingDeleteChat;
    if (!id) return;
    setConfirmingDeleteChat(null);
    await handleDelete(id);
  }, [confirmingDeleteChat, handleDelete]);

  const handlePickTopic = useCallback((topic: { prompt: string; skillIds?: string[] }) => {
    if (topic.prompt) useStore.getState().setDraftText("new", topic.prompt);
    const ids = topic.skillIds ?? [];
    if (ids.length > 0) {
      void useStore.getState().setActivePinnedSkill(ids[0]);
      if (ids.length > 1) void useStore.getState().setActiveActivatedSkills(ids.slice(1));
    }
  }, []);

  const activeConv = activeId ? conversations.find((c) => c.id === activeId) ?? null : null;
  const activeTitle = activeConv?.title ?? null;
  const backgroundBusy = activeRunChatIds.some((id) => id !== activeId);

  const isEmpty = displayMessages.length === 0;
  const activeContextLength = models.find((m) => m.id === activeModel)?.contextLength ?? null;
  const persistedUsage = (() => {
    for (let i = displayMessages.length - 1; i >= 0; i--) {
      const stats = displayMessages[i].stats;
      if (displayMessages[i].role === "assistant" && stats) {
        const ctxLen = stats.contextLength ?? models.find((m) => m.id === stats.model)?.contextLength ?? activeContextLength;
        return { promptTokens: stats.promptTokens, contextLength: ctxLen };
      }
    }
    return null;
  })();
  const usage = lastUsage ?? persistedUsage;
  const ctxPercent = usage && usage.contextLength
    ? Math.round((usage.promptTokens / usage.contextLength) * 100)
    : null;

  if (initError) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-4 text-center">
        <div className="max-w-md flex flex-col gap-3">
          <h1 className="text-base font-semibold">Couldn't load app data</h1>
          <p className="text-xs font-mono whitespace-pre-wrap text-destructive break-all">{initError}</p>
          <p className="text-xs text-muted-foreground">
            Backend: <code>{BACKEND_URL || "(relative)"}</code>
          </p>
          <div className="flex gap-2 justify-center">
            <button onClick={() => location.reload()} className="rounded-md bg-primary px-3 py-2 text-sm text-primary-foreground">Retry</button>
            <button
              onClick={() => { localStorage.removeItem("llm-api-key"); location.reload(); }}
              className="rounded-md border px-3 py-2 text-sm hover:bg-muted"
            >
              Reset API key
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!loaded) {
    return (
      <div className="flex min-h-dvh items-center justify-center text-xs text-muted-foreground">
        Loading…
      </div>
    );
  }

  return (
    <div className="relative flex overflow-x-hidden overflow-y-visible">
      <div
        className={`flex flex-1 flex-col min-w-0 ${artifactPanelOpen ? "lg:mr-[32rem]" : ""}`}
        style={{ paddingLeft: "var(--sb-w, 0px)" }}
      >
        <ChatHeader
          title={activeTitle ?? "Libre LM Studio"}
          hasActiveChat={!!activeId && !!activeConv}
          currentFolderId={activeConv?.folderId ?? null}
          pinned={!!activeConv?.pinned}
          folders={folders}
          sidebarOpen={sidebarOpen}
          backgroundBusy={backgroundBusy}
          artifactPanelOpen={artifactPanelOpen}
          ctxPercent={ctxPercent}
          lastUsage={usage}
          activeContextLength={activeContextLength}
          onShowSidebar={() => setSidebarOpen(true)}
          onNew={handleNew}
          onRename={() => activeConv && setRenamingChat({ id: activeConv.id, draft: activeConv.title })}
          onSuggestTitle={handleSuggestTitle}
          onTogglePin={(pinned) => { if (activeId) togglePin(activeId, pinned); }}
          onMove={(folderId) => { if (activeId) moveChat(activeId, folderId); }}
          onDelete={() => activeId && setConfirmingDeleteChat(activeId)}
          onOpenArtifacts={() => openArtifactPanel(null)}
          onContinueInNew={handleContinueInNew}
          continueBusy={continueBusy}
          suggestBusy={suggestBusy}
        />
        {showApiKey && <ApiKeyDialog onClose={() => setShowApiKey(false)} />}
        {confirmingDeleteChat && (
          <ConfirmDialog
            title="Delete this chat?"
            message="This can't be undone."
            confirmLabel="Delete"
            destructive
            onConfirm={confirmDeleteChat}
            onCancel={() => setConfirmingDeleteChat(null)}
          />
        )}
        {renamingChat && (
          <RenameChatDialog
            draft={renamingChat.draft}
            onChange={(v) => setRenamingChat({ ...renamingChat, draft: v })}
            onSubmit={handleRenameSubmit}
            onCancel={() => setRenamingChat(null)}
          />
        )}

        {isEmpty ? (
          <div className="flex min-h-dvh flex-col items-center justify-center gap-4 px-4 py-10">
            <HomeIntro
              conversations={conversations}
              folders={folders}
              activeModel={activeModel}
              models={models}
              onPickTopic={handlePickTopic}
              onSelectChat={handleSelect}
            />
            <div className="w-full max-w-2xl flex flex-col gap-2">
              <ChatInput onSend={sendMessage} isStreaming={isStreaming} autoFocus draftKey="new" topSlot={<RequestBar isStreaming={isStreaming} />} />
            </div>
          </div>
        ) : (
          <>
            <MessageList
              messages={displayMessages}
              isStreaming={isStreaming}
              onStop={stop}
              onReload={reloadLast}
              activeId={activeId}
              onOpenArtifact={(id) => openArtifactPanel(id)}
              inlineArtifactExpanded={inlineArtifactExpanded}
              onToggleInlineArtifact={() => setInlineArtifactExpanded((v) => !v)}
            />
            <ChatInput onSend={sendMessage} isStreaming={isStreaming} draftKey={activeId ?? "new"} topSlot={<RequestBar isStreaming={isStreaming} />} />
          </>
        )}
      </div>
      <ArtifactStrip
        onOpenLibrary={() => {
          useStore.getState().closeArtifactPanel();
          navigateRoute("/library");
        }}
      />
    </div>
  );
}
